import { useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Empty as AntdEmpty, Button, Select, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  DashboardProcsContext,
  useProcessingState,
  ProcessingType,
} from '@/entities/proc';
import { ProcProcessingState } from '@/entities/proc/proc-generation.types';
import {
  SortOption,
  SORT_OPTIONS,
  useSortedProcs,
} from '@/entities/proc/sorting';
import { useAuth } from '@/features/auth';
import { useNotificationContext } from '@/features/notifications';
import { api } from '@/shared/api';
import { useRouterPrivateContext } from '@/shared/router';
import { captureError } from '@/shared/utils/error-reporting';
import { ProcCard } from '@/widgets/proc-card';
import { GeneratingProcContent } from '@/widgets/proc-card/GeneratingProcContent';
import EmptyIcon from '@assets/empty.svg';

interface ErrorResponse {
  response: {
    data: {
      message:
        | {
            code: string;
          }
        | string;
    };
    status?: number;
  };
}

function isWorkspaceError(error: unknown): error is ErrorResponse {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return false;
  }

  const response = (error as { response: unknown }).response;
  if (
    typeof response !== 'object' ||
    response === null ||
    !('data' in response)
  ) {
    return false;
  }

  const data = (response as { data: unknown }).data;
  if (typeof data !== 'object' || data === null || !('message' in data)) {
    return false;
  }

  const message = (data as { message: unknown }).message;

  if (typeof message === 'string') {
    return true;
  }

  if (typeof message === 'object' && message !== null && 'code' in message) {
    return typeof (message as { code: unknown }).code === 'string';
  }

  return false;
}

const PROCS_PER_PAGE = 50;

const getSortByField = (sortOption: SortOption): string | undefined => {
  const sortMapping = {
    [SortOption.ALPHABETICAL]: 'title',
    [SortOption.NEWEST]: 'createdAt',
  };
  return sortMapping[sortOption];
};

export const WorkspaceProcsTab = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { accessToken } = useAuth();
  const { selectedCategory, selectedWorkspace } = useRouterPrivateContext();
  const { showNotification } = useNotificationContext();
  const queryClient = useQueryClient();
  const [selectedSortOption, setSelectedSortOption] = useState<SortOption>(
    SortOption.NEWEST,
  );

  const { processingStates: processingProcs } =
    useProcessingState<ProcProcessingState>(ProcessingType.PROC_PROCESSING, {
      onSuccess: () => {
        showNotification({
          type: 'success',
          message: 'Proc generated successfully',
          description: 'The proc has been generated',
        });
        queryClient.invalidateQueries(['WorkspaceProcsGetPaginated']);
      },
      onError: (error) => {
        showNotification({
          type: 'error',
          message: 'Failed to generate proc',
          description: error,
        });
        queryClient.invalidateQueries(['WorkspaceProcsGetPaginated']);
      },
    });

  async function fetchProcs({ pageParam = 1 }) {
    return await api.get(
      `/procs/workspaces/${selectedWorkspace?.id || params?.workspaceId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          page: pageParam,
          take: PROCS_PER_PAGE,
          sortBy: getSortByField(selectedSortOption),
          order: selectedSortOption === SortOption.NEWEST ? 'DESC' : 'ASC',
        },
      },
    );
  }

  const {
    data: procsData,
    refetch: refetchProcs,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'WorkspaceProcsGetPaginated',
      selectedCategory,
      selectedWorkspace?.id || params?.workspaceId,
    ],
    queryFn: fetchProcs,
    keepPreviousData: true,
    getNextPageParam: ({ data }) => {
      if (data.meta.hasNextPage) {
        return data.meta.page + 1;
      }
      return false;
    },
    enabled:
      Boolean(
        selectedCategory === 'myProcs' &&
          (selectedWorkspace?.id || params?.workspaceId),
      ) || Boolean(selectedCategory && selectedCategory !== 'myProcs'),
    onSuccess() {
      if (
        location.pathname === '/dashboard' ||
        location.pathname === '/dashboard/'
      ) {
        navigate(`/dashboard/workspaces/${selectedWorkspace?.id}`, {
          replace: true,
        });
      }
    },
    onError(error: unknown) {
      if (isWorkspaceError(error)) {
        const message = error.response.data.message;

        if (
          typeof message === 'object' &&
          message.code === 'wrong_workspace_id'
        ) {
          navigate('/dashboard/workspaces');
          captureError(error, {
            module: 'workspace_procs_tab_on_error_wrong_workspace_id',
            action: 'navigate_to_workspaces',
          });
        }
        if (
          typeof message === 'string' &&
          message === 'Validation failed (uuid is expected)'
        ) {
          navigate('/dashboard/workspaces');
          captureError(error, {
            module: 'workspace_procs_tab_on_error_wrong_workspace_id',
            action: 'navigate_to_workspaces',
          });
        }
      }
    },
  });

  const sortedProcs = useSortedProcs(procsData, selectedSortOption);

  if (!procsData) {
    return null;
  }

  return sortedProcs.length > 0 ? (
    <DashboardProcsContext.Provider value={{ refetchProcs }}>
      <>
        <div style={{ padding: '0.25px', marginBottom: 5 }}>
          <Typography.Text style={{ marginRight: 8 }}>Sort by:</Typography.Text>
          <Select
            defaultValue={SortOption.NEWEST}
            options={SORT_OPTIONS}
            style={{ width: 200, marginBottom: 10 }}
            onChange={(value) => setSelectedSortOption(value as SortOption)}
          />
        </div>
        <Grid data-testid="procs-grid">
          {sortedProcs.map((item) => {
            if (item.state === 'generating' || item.state === 'regenerating') {
              const procState = processingProcs[item.id];
              return (
                <GeneratingProcContent
                  key={item.id}
                  isError={procState?.isError}
                  prompt={procState?.prompt || item.description}
                  progress={procState?.progress}
                  type={ProcessingType.PROC_PROCESSING}
                />
              );
            }
            return (
              <ProcCard
                key={item.id}
                type={
                  selectedCategory === 'knowledgeBase'
                    ? 'myProcs'
                    : selectedCategory!
                }
                proc={item}
              />
            );
          })}
        </Grid>
        {hasNextPage && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              margin: '50px 0 20px 0',
            }}
          >
            <Button type="primary" onClick={() => fetchNextPage()}>
              Load more
            </Button>
          </div>
        )}
      </>
    </DashboardProcsContext.Provider>
  ) : (
    <Empty
      data-testid="workspace-procs-empty"
      image={EmptyIcon}
      styles={{ image: { height: '100%', maxHeight: 180, marginInline: 0 } }}
      description="There are no procs to show"
      style={{
        width: '100%',
        height: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    />
  );
};

const Grid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  grid-gap: 20px;
  align-items: stretch;
`;

const Empty = styled(AntdEmpty)`
  margin-inline: 0px;
`;
